// Wzór na pole trójkąta: P = 1/2 * podstawa * wysokość
// Typ: FindAreaFromBaseAndHeightTask

import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import { InlineMath, BlockMath } from "react-katex";

// utils
const rnd = (a, b) => Math.floor(Math.random() * (b - a + 1)) + a;

function gcd(a, b) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 1;
  a = Math.abs(a); b = Math.abs(b);
  while (b !== 0) { const t = b; b = a % b; a = t; }
  return a || 1;
}

// ładny zapis ułamka (całkowita / zwykła / mieszana), jak w FindSlopeTask
function formatFraction(numerator, denominator) {
  if (denominator === 0) return "\\text{—}";
  const sign = Math.sign(numerator) * Math.sign(denominator);
  const num = Math.abs(numerator);
  const den = Math.abs(denominator);
  const d = gcd(num, den);
  const simpNum = num / d;
  const simpDen = den / d;

  if (simpDen === 1) return `${sign * simpNum}`;
  if (simpNum < simpDen) {
    // ułamek właściwy, np. -3/5
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }
  const whole = Math.floor(simpNum / simpDen);
  const rem = simpNum % simpDen;
  if (rem === 0) return `${sign * whole}`;
  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${rem}}{${simpDen}}`;
}

export function generateTask() {
  // losujemy którą podstawę i wysokość damy
  const variants = [
    { base: "a", height: "h_a" },
    { base: "b", height: "h_b" },
    { base: "c", height: "h_c" },
  ];
  const chosen = variants[rnd(0, variants.length - 1)];

  const baseVal = rnd(6, 24);
  const heightVal = rnd(5, 22);

  return {
    type: "FindAreaFromBaseAndHeightTask",
    data: {
      baseName: chosen.base,
      heightName: chosen.height,
      baseVal,
      heightVal,
    }
  };
}

export function renderTask({ data }) {
  const { baseName, heightName, baseVal, heightVal } = data;
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        W trójkącie <InlineMath math={'ABC'} /> dana jest podstawa
        <InlineMath math={`\\;${baseName}=${baseVal}`} /> oraz wysokość
        <InlineMath math={`\\;${heightName}=${heightVal}`} /> opuszczona na tę podstawę.
        Oblicz pole trójkąta <InlineMath math={'P'} />.
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const val = userAnswer ?? { P: { raw: "" } };
  return (
    <div style={{ display:"grid", gap: 10, marginTop: 8 }}>
      <FractionInput
        value={val.P}
        onChange={(v)=> onChange({ ...(userAnswer ?? {}), P: v })}
        disabled={disabled}
        label="P"
        placeholder="wpisz wynik"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.P) return false;
  const given = userAnswer.P.parsed;
  if (given == null || !Number.isFinite(given)) return false;

  const { baseVal, heightVal } = data;
  const expected = 0.5 * baseVal * heightVal;
  return Math.abs(given - expected) <= 1e-9;
}

export function renderAnswer({ data }) {
  const { baseVal, heightVal } = data;
  const prod = baseVal * heightVal;   // licznik przed /2
  const mixed = formatFraction(prod, 2); // ładny zapis mieszany
  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={`P = ${mixed}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { baseName, heightName, baseVal, heightVal } = data;
  const prod = baseVal * heightVal;
  const d = gcd(prod, 2);
  const sn = prod / d;  // uproszczony licznik
  const sd = 2 / d;     // uproszczony mianownik
  const mixed = formatFraction(prod, 2);

  return (
    <div style={{ textAlign:"left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wzór na pole trójkąta:</div>
      <BlockMath math={`P = \\tfrac{1}{2}\\,${baseName}\\,${heightName}`} />

      <div><strong>Krok 2.</strong> Podstawiamy dane:</div>
      <BlockMath math={`P = \\tfrac{1}{2}\\cdot ${baseVal}\\cdot ${heightVal}`} />

      <div><strong>Krok 3.</strong> Obliczamy iloczyn i zapisujemy jako ułamek:</div>
      <BlockMath math={`P = \\dfrac{${prod}}{2}`} />

      {sd === 1 ? (
        <BlockMath math={`P = ${sn}`} />
      ) : (
        <>
          <div>Upraszczamy i zapisujemy jako liczbę mieszaną:</div>
          <BlockMath math={`P = ${mixed}`} />
        </>
      )}
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.P?.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latex = formatLatexPreview(userAnswer.P.raw, "P");

  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={latex} />
    </div>
  );
}
